import { useQuery } from "@tanstack/react-query";

import { useRestApi } from "@/context/api";
import { useCurrentCompany } from "@/context/auth";
import { queryKeys } from "@/queries/queryKeys";

/** Never served from cache: the check is a diagnostic, so every opening of the tab asks SkillPanel again. */
export const useUserMapSyncQuery = (userId: string, enabled: boolean) => {
  const { getUserMapSync } = useRestApi().mapSync;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.USER_MAP_SYNC, currentCompany.id, userId],
    queryFn: () => getUserMapSync(currentCompany.id, userId),
    enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
};

/**
 * How SkillPanel shows the user, for one row of the profile. Not retried: the row falls back to
 * what UserManager knows, so a second wait on a silent SkillPanel buys nothing.
 */
export const useSkillPanelVisibilityQuery = (userId: string) => {
  const { getSkillPanelVisibility } = useRestApi().mapSync;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.SKILLPANEL_VISIBILITY, currentCompany.id, userId],
    queryFn: () => getSkillPanelVisibility(currentCompany.id, userId),
    retry: false,
  });
};

/**
 * SkillPanel's values for the read-only custom fields UserManager holds no value for. Asked only
 * when such a field exists, and after the profile has rendered. Not retried, for the same reason as
 * the visibility query.
 */
export const useSkillPanelCustomFieldsQuery = (userId: string, enabled: boolean) => {
  const { getSkillPanelCustomFields } = useRestApi().mapSync;
  const currentCompany = useCurrentCompany();

  return useQuery({
    queryKey: [queryKeys.SKILLPANEL_CUSTOM_FIELDS, currentCompany.id, userId],
    queryFn: () => getSkillPanelCustomFields(currentCompany.id, userId),
    enabled,
    retry: false,
  });
};
